<template>
    <div :class="containerClassName" v-if="selectedConversation?.id">
        <div class="flex items-center">
            <template v-if="isSlideover">
                <div class="flex grow min-w-0 flex-col">
                    <span
                        class="font-semibold text-gray-900"
                        :class="headingFontSizeClassName"
                        data-testid="header-name"
                        v-if="selectedConversationDisplayLead?.fullName"
                    >
                        {{ selectedConversationDisplayLead.fullName }}
                    </span>

                    <span
                        class="text-gray-500"
                        :class="headingFontSizeClassName"
                        data-testid="header-phone"
                    >
                        {{ formattedLeadPhoneWithType }}
                    </span>
                </div>
            </template>

            <template v-else>
                <sms-avatar
                    data-testid="header-avatar"
                    size="lg"
                    :username="selectedConversationDisplayLead?.fullName || ''"
                />

                <div class="ml-3 flex flex-col">
                    <div class="flex items-center gap-x-2">
                        <span
                            class="font-semibold"
                            :class="headingFontSizeClassName"
                            data-testid="header-name"
                            v-if="selectedConversationDisplayLead?.fullName"
                        >
                            {{ selectedConversationDisplayLead.fullName }}
                        </span>

                        <span
                            class="text-gray-500"
                            :class="headingFontSizeClassName"
                            data-testid="header-phone"
                        >
                            {{ formattedLeadPhoneWithType }}
                        </span>

                        <sms-group-badge v-if="selectedConversation.isGroup" />

                        <sms-archived-badge
                            data-testid="archived-badge"
                            v-if="selectedConversation.archived"
                        />
                    </div>

                    <span class="text-sm text-gray-500" data-testid="sent-from-dealer-phone">
                        {{
                            t('smsCommunication.sentFromDealerPhone', {
                                phone: formattedDealerPhone || '—',
                            })
                        }}
                    </span>
                </div>
            </template>
        </div>

        <div class="flex items-center gap-x-2">
            <activix-tooltip
                data-testid="archive-button-tooltip"
                :content="archiveTooltip"
                v-if="!selectedConversationReadOnly"
            >
                <button
                    class="p-2 text-gray-600 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isArchiveDisabled"
                    data-testid="archive-button"
                    @click="toggleArchived"
                >
                    <icon name="regular/undo" v-if="isArchived" />
                    <icon name="regular/archive-folder" v-else />
                </button>
            </activix-tooltip>

            <!-- Conversation members avatars -->
            <activix-tooltip
                :content="t('smsCommunication.conversationDetails')"
                v-if="!showConversationDetails && !isSlideover && conversationMembers.length > 0"
            >
                <sms-conversation-members-avatars
                    class="cursor-pointer"
                    :members="conversationMembers"
                    @click="emit('toggle-show-conversation-details', !showConversationDetails)"
                />
            </activix-tooltip>

            <!-- Redirect -->
            <activix-tooltip
                data-testid="open-lead-button-tooltip"
                :content="t('smsCommunication.redirectToLead')"
                v-if="!isSlideover"
            >
                <button
                    class="p-2 text-gray-600 hover:text-blue-500"
                    data-testid="open-lead-button"
                    @click="openLeadPage"
                >
                    <icon name="regular/open-external-link" />
                </button>
            </activix-tooltip>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { storeToRefs } from 'pinia';
    import { useNiotextStore } from '@/store/modules/niotext/store.ts';
    import { useRouter } from 'vue-router';
    import { useI18n } from '@/hooks/useI18n.ts';
    import { useNotify } from '@/hooks/useNotify.ts';
    import { usePhoneType } from '@/hooks/usePhoneType.ts';
    import { formatPhone } from '@/utils/index.js';
    import SmsArchivedBadge from '@/components/sms/SmsArchivedBadge.vue';
    import SmsAvatar from '@/components/sms/SmsAvatar.vue';
    import SmsGroupBadge from '@/components/sms/SmsGroupBadge.vue';
    import SmsConversationMembersAvatars from '@/components/sms/SmsConversationMembersAvatars.vue';
    import { SmsChatConversationHeaderVariant } from '@/components/sms/SmsChatConversationHeader.types.ts';
    import { useConversationMembers } from '@/hooks/useConversationMembers.ts';

    interface Props {
        showConversationDetails?: boolean;
        variant?: SmsChatConversationHeaderVariant;
    }

    interface Emits {
        (e: 'toggle-show-conversation-details', value: boolean): void;
    }

    const props = withDefaults(defineProps<Props>(), {
        variant: SmsChatConversationHeaderVariant.DEFAULT,
    });

    const emit = defineEmits<Emits>();

    const router = useRouter();
    const { t } = useI18n();
    const { notify } = useNotify();
    const { formatPhoneType } = usePhoneType();
    const { conversationMembers } = useConversationMembers();

    const niotextStore = useNiotextStore();
    const {
        currentLeadId,
        selectedConversation,
        selectedConversationDisplayLead,
        selectedConversationDisplayPhone,
        selectedConversationReadOnly,
    } = storeToRefs(niotextStore);

    const isSlideover = computed<boolean>(() => {
        return [
            SmsChatConversationHeaderVariant.SLIDEOVER,
            SmsChatConversationHeaderVariant.SLIDEOVER_COLLAPSED,
        ].includes(props.variant);
    });

    const isSlideoverCollapsed = computed<boolean>(
        () => props.variant === SmsChatConversationHeaderVariant.SLIDEOVER_COLLAPSED
    );

    const headingFontSizeClassName = computed(() => {
        return {
            'text-lg': !isSlideoverCollapsed.value,
            'text-sm': isSlideoverCollapsed.value,
        };
    });

    const isArchiving = ref<boolean>(false);

    const isArchived = computed<boolean>(() => selectedConversation.value?.archived ?? false);

    const isArchiveDisabled = computed<boolean>(
        () => isArchiving.value || isDraftConversation.value
    );

    const isDraftConversation = computed<boolean>(
        () => !selectedConversation.value || selectedConversation.value.id < 0
    );

    const archiveTooltip = computed<string>(() => {
        if (isArchived.value) {
            return t('smsCommunication.restoreConversation');
        }

        return t('smsCommunication.archive');
    });

    const containerClassName = computed(() => [
        'flex items-center justify-between w-full',
        {
            'p-4 border-b border-gray-200 bg-white': !isSlideover.value,
            'gap-x-4 px-0 py-2': isSlideover.value,
        },
    ]);

    const formattedDealerPhone = computed<string>(() => {
        const dealerPhone = selectedConversation.value?.smsProvider?.phone ?? '';

        if (!dealerPhone) {
            return '';
        }

        return formatPhone(dealerPhone) || dealerPhone;
    });

    const formattedLeadPhone = computed<string>(() => {
        const leadPhone = selectedConversationDisplayPhone.value?.phone ?? '';

        if (!leadPhone) {
            return '';
        }

        return formatPhone(leadPhone) || leadPhone;
    });

    const formattedLeadPhoneWithType = computed<string>(() => {
        const type = selectedConversationDisplayPhone.value?.type;

        if (!formattedLeadPhone.value) {
            return '—';
        }

        if (type) {
            return `${formatPhoneType(type)}: ${formattedLeadPhone.value}`;
        }

        return formattedLeadPhone.value;
    });

    // Actions
    const openLeadPage = () => {
        if (!currentLeadId.value) {
            return;
        }

        const route = router.resolve({
            name: 'leads.update',
            params: {
                id: String(currentLeadId.value),
            },
        });

        window.open(route.href, '_blank');
    };

    const toggleArchived = async () => {
        if (isDraftConversation.value || isArchiving.value || !selectedConversation.value) {
            return;
        }

        const isArchivingAction = !selectedConversation.value.archived;

        try {
            isArchiving.value = true;

            await niotextStore.toggleArchived(selectedConversation.value.id);

            if (isArchivingAction) {
                notify.success(t('smsCommunication.notifications.conversationArchivedSuccess'));
            } else {
                notify.success(t('smsCommunication.notifications.conversationUnarchivedSuccess'));
            }
        } catch {
            if (isArchivingAction) {
                notify.error(t('smsCommunication.notifications.archiveFailed'));
            } else {
                notify.error(t('smsCommunication.notifications.unarchiveFailed'));
            }
        } finally {
            isArchiving.value = false;
        }
    };
</script>
